import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@components/atoms/Accordion'
import { Avatar } from '@components/atoms/Avatar'
import cn from '@components/utils/cn'
import Icon, { type IconTypes } from '@components/utils/Icon'
import { scopedTranslation } from '@utils/I18n'

import { PublishNotificationKind } from '@frontend/graphql/types.generated'
import type { PublishRecipient } from './usePublishPreviewQuery'

const t = scopedTranslation('components.publish_roster')
const tBoard = scopedTranslation('roster_board')

type Outcome = {
  kind: PublishNotificationKind
  labelKey: string
  icon: IconTypes
  tone: string
}

const OUTCOMES: Outcome[] = [
  {
    kind: PublishNotificationKind.Unassigned,
    labelKey: 'kind_unassigned',
    icon: 'x-circle',
    tone: 'tw:text-error-500',
  },
  {
    kind: PublishNotificationKind.Assigned,
    labelKey: 'kind_assigned',
    icon: 'user-plus-01',
    tone: 'tw:text-success-500',
  },
  { kind: PublishNotificationKind.Changed, labelKey: 'kind_changed', icon: 'pencil-01', tone: 'tw:text-warning-500' },
]

function VolunteerChips({ recipients }: { recipients: PublishRecipient[] }) {
  return (
    <ul className="tw:flex tw:flex-wrap tw:gap-1.5">
      {recipients.map((recipient) => (
        <li
          key={recipient.personId}
          className="tw:inline-flex tw:items-center tw:gap-1.5 tw:rounded-full tw:border tw:border-gray-200 tw:bg-white tw:py-0.5 tw:pl-0.5 tw:pr-2.5 tw:text-xs tw:font-medium tw:text-gray-700"
        >
          <Avatar name={recipient.fullName ?? ''} />
          {recipient.fullName ?? tBoard('changes.unknown_volunteer')}
        </li>
      ))}
    </ul>
  )
}

export function NotifiedVolunteers({ recipients }: { recipients: PublishRecipient[] }) {
  const groups = OUTCOMES.map((outcome) => ({
    outcome,
    members: recipients.filter((recipient) => recipient.kind === outcome.kind),
  })).filter((group) => group.members.length > 0)

  if (groups.length === 0) return null

  return (
    <div className="tw:flex tw:flex-col tw:gap-2">
      <div className="tw:flex tw:items-baseline tw:justify-between tw:gap-2.5">
        <span className="tw:text-sm tw:font-medium tw:text-gray-900">
          {t('email_lead', { count: recipients.length })}
        </span>
        <span className="tw:text-xs tw:text-gray-500">{t('names_hint')}</span>
      </div>

      <Accordion type="multiple" className="tw:overflow-hidden tw:rounded-lg tw:border tw:border-gray-200">
        {groups.map(({ outcome, members }) => (
          <AccordionItem
            key={outcome.kind}
            value={outcome.kind}
            className="tw:border-b tw:border-gray-200 tw:last:border-b-0"
          >
            <AccordionTrigger className="tw:gap-2.5 tw:px-3 tw:py-2.5 tw:data-[state=closed]:hover:bg-gray-50 tw:hover:no-underline">
              <Icon type={outcome.icon} className={cn('tw:h-3.5 tw:w-3.5 tw:shrink-0', outcome.tone)} aria-hidden />
              <span className="tw:text-sm tw:font-medium tw:text-gray-900">{t(outcome.labelKey)}</span>
              <span className="tw:ml-auto tw:text-xs tw:tabular-nums tw:text-gray-500">{members.length}</span>
            </AccordionTrigger>
            <AccordionContent className="tw:px-3 tw:pb-3 tw:pl-9 tw:pt-1">
              <VolunteerChips recipients={members} />
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  )
}
